import {
  OnlineFourBucketPartnerGroupKey,
  OnlinePartnerBucketGroups,
  PricingTemplate,
} from "../../../interfaces/partner.interface";
import { OnlinePricingTemplate } from "../../../interfaces/partnerDeal.interface";
import {
  OnlineBucketGroupCoverageEntry,
  OnlineGroupKey,
  OnlinePricingStructure,
} from "../../../interfaces/onlinePricing.interface";
import {
  onlineBucketGroupKeysForTemplates,
  orderOnlineGroupKeys,
  storedOnlineGroupKeys,
} from "../../businesses/utils/sixteenBucketOnlinePricing";
// Single source of truth for the online bucket keys. Any code that
// iterates online buckets should import this list.
export const ONLINE_BUCKET_KEYS = [
  "iDeal",
  "bancontact",
  "amex",
  "corporateCreditCards",
  "nonCorporateCreditCards",
  "appleGooglePay",
] as const;

export type OnlineBucketKey = (typeof ONLINE_BUCKET_KEYS)[number];

// Human-readable label per bucket, for tile/row rendering across the codebase.
// Casing matches onlineGroupLabel, which the partner wizard and the deal wizard
// now share because it labels both shapes' groups.
export const ONLINE_BUCKET_LABELS: Record<OnlineBucketKey, string> = {
  iDeal: "iDEAL",
  bancontact: "Bancontact",
  amex: "AMEX",
  corporateCreditCards: "Corporate Credit Cards",
  nonCorporateCreditCards: "Non-Corporate Credit Cards",
  appleGooglePay: "Apple Pay / Google Pay",
};

// Compile-time guards (TS 4.6 has no `satisfies`):
// - `_Missing` errors if the four-bucket shape gains a key not listed here.
// - `_Extra` errors if a typo lands in ONLINE_BUCKET_KEYS.
// Checked against the four-bucket key union rather than the storage type, which
// carries both shapes' keys and so would wave anything through.
type _Missing = Exclude<OnlineFourBucketPartnerGroupKey, OnlineBucketKey>;
type _Extra = Exclude<OnlineBucketKey, OnlineFourBucketPartnerGroupKey>;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _ENSURE_EXHAUSTIVE: _Missing = undefined as never;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _ENSURE_NO_EXTRA: _Extra = undefined as never;

// Local-payment-method bucket per country's online pricing template. NL keeps iDEAL,
// BE swaps to Bancontact, every other set country drops the local method (CardsOnly).
// One implementation for both rails, next to the group model that reads it.
export { onlinePricingTemplateForCountry } from "../../businesses/utils/sixteenBucketOnlinePricing";

// Card buckets + Apple/Google Pay are always editable regardless of template; only the
// local-payment-method bucket (iDeal vs bancontact vs none) varies.
const CARD_AND_WALLET_BUCKET_KEYS: readonly OnlineBucketKey[] = [
  "amex",
  "corporateCreditCards",
  "nonCorporateCreditCards",
  "appleGooglePay",
];

export function getOnlineBucketKeysForTemplate(
  template: OnlinePricingTemplate | undefined
): readonly OnlineBucketKey[] {
  switch (template) {
    case OnlinePricingTemplate.BancontactBE:
      return ["bancontact", ...CARD_AND_WALLET_BUCKET_KEYS];
    case OnlinePricingTemplate.CardsOnly:
      return CARD_AND_WALLET_BUCKET_KEYS;
    default:
      return ["iDeal", ...CARD_AND_WALLET_BUCKET_KEYS];
  }
}

// ── The 16-bucket group model ───────────────────────────────────────────────
// A partner priced on sixteen buckets carries the in-person group names instead
// of the four-bucket card keys, on the same fields. Two independent axes decide
// the set: the card split (declared per range, see onlineRangeCardSplit) and the
// local payment method (country-driven, the OnlinePricingTemplate above).
//
// Coverage from the read wins when present, so a stored extra group is not
// dropped. Create and a template switch have none, and then the selected
// split names the rows. Callers without a split still fall back to stored keys.

// Keys exclusive to one shape. The backend reads exactly these to decide which
// structure a stored section is on; `amex`, `appleGooglePay`, `iDeal` and
// `bancontact` are shared and prove nothing on their own.
export const ONLINE_SIXTEEN_BUCKET_ONLY_KEYS: readonly OnlineGroupKey[] = [
  "domesticDebitCards",
  "domesticCreditCards",
  "europeanDebitCards",
  "european",
  "internationalAndCommercial",
];

export const ONLINE_FOUR_BUCKET_ONLY_KEYS: readonly OnlineGroupKey[] = [
  "corporateCreditCards",
  "nonCorporateCreditCards",
];

/** Which structure a stored online section is on, or undefined when its keys
 *  prove neither (an empty section, or one holding only shared keys). Mirrors
 *  the backend's detectOnlinePricingStructure — the same positive-evidence
 *  rule, so KBM and the save agree on what a partner is. */
export function detectOnlinePartnerPricingStructure(
  ...sections: (OnlinePartnerBucketGroups | undefined)[]
): OnlinePricingStructure | undefined {
  for (const section of sections) {
    if (!section) continue;
    const has = (key: OnlineGroupKey): boolean => section[key] != null;
    if (ONLINE_SIXTEEN_BUCKET_ONLY_KEYS.some(has))
      return OnlinePricingStructure.SixteenBucket;
    if (ONLINE_FOUR_BUCKET_ONLY_KEYS.some(has))
      return OnlinePricingStructure.FourBucket;
  }
  return undefined;
}

/** The 16-bucket groups from the read, or the selected template pair, or stored keys. */
export function getOnlineSixteenBucketGroupKeys(params: {
  coverage?: OnlineBucketGroupCoverageEntry[];
  stored?: OnlinePartnerBucketGroups;
  cardSplit?: string;
  template?: OnlinePricingTemplate;
}): readonly OnlineGroupKey[] {
  if (params.coverage?.length) {
    return orderOnlineGroupKeys(
      params.coverage.map((entry) => entry.group).filter(Boolean)
    );
  }
  if (params.cardSplit != null && params.template != null) {
    return orderOnlineGroupKeys([
      ...onlineBucketGroupKeysForTemplates(params.cardSplit, params.template),
      "appleGooglePay",
    ]);
  }
  return storedOnlineGroupKeys(params.stored);
}

/** The groups a partner section is priced on, whichever shape it is in. The one
 *  entry point for rendering, validating and serializing partner and deal
 *  online pricing, so all three always agree on the key set. */
export function getOnlinePartnerGroupKeys(params: {
  structure: OnlinePricingStructure;
  template: OnlinePricingTemplate | undefined;
  coverage?: OnlineBucketGroupCoverageEntry[];
  stored?: OnlinePartnerBucketGroups;
  cardSplit?: string;
}): readonly OnlineGroupKey[] {
  const { structure, template, coverage, stored, cardSplit } = params;
  return structure === OnlinePricingStructure.SixteenBucket
    ? getOnlineSixteenBucketGroupKeys({
        coverage,
        stored,
        cardSplit,
        template,
      })
    : getOnlineBucketKeysForTemplate(template);
}

// The one in-person profile the card split is read off. A range under any other
// name prices a subset of merchants and never decides the axis, whichever range
// happens to be open in the wizard.
export const CARD_SPLIT_PROFILE_NAME = "default";

/** Whether an in-person profile is the one a save resolves the split from. A
 *  profile KBM has not persisted yet carries no name and is written as
 *  "default", so it counts. */
export const isCardSplitProfile = (name?: string): boolean =>
  !name || name === CARD_SPLIT_PROFILE_NAME;

/** The card split an online price range is priced on. Declared per range rather
 *  than derived from the in-person rail: the two axes move independently, so a
 *  partner can price its card populations one way in person and another online.
 *  A range that declares nothing prices the Default split, which is why no
 *  stored range needs a backfill — read it the way the backend's
 *  resolveOnlineCardSplitTemplate does, or KBM renders groups the save refuses. */
export function onlineRangeCardSplit(range?: {
  onlineCardSplitTemplate?: PricingTemplate;
}): PricingTemplate {
  return range?.onlineCardSplitTemplate ?? PricingTemplate.Default;
}

/** The in-person card split, still read off the `default` profile. Online no
 *  longer follows it — see onlineRangeCardSplit — so the only remaining caller
 *  is the partner mapper, which has its own follow-up. */
export function partnerOnlineCardSplit(partner?: {
  purchPriceProfiles?: { name?: string; pricingTemplate?: PricingTemplate }[];
}): PricingTemplate {
  const defaultProfile = partner?.purchPriceProfiles?.find(
    (profile) => profile.name === CARD_SPLIT_PROFILE_NAME
  );
  return defaultProfile?.pricingTemplate ?? PricingTemplate.Default;
}
